import { AiChatbotConversation } from '@va/dashboard/shared/ai-assistant';
import { FolderIcon, PencilEditIcon, TrashCanIcon } from '@va/icons';
import { useLocalizationContext, useTranslate } from '@va/localization';
import { useConfirmationDialogContext } from '@va/shared/util-confirmation';
import { Button, Paragraph, ParagraphWithTooltip } from '@va/ui/design-system';
import { addNumberSeparator } from '@va/util/helpers';
import { useAriaListboxPattern } from '@va/util/misc';
import classNames from 'classnames';
import { FocusEvent, MouseEventHandler, useCallback, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useConversations, useDeleteConversation } from '../../api';
import { useGroupedChatsByInterval } from '../../helpers';
import { AiChatbotView } from '../../types';
import { useAiChatbotContext } from '../aiChatbotContext';
import { DeleteConversationConfirmationModal } from './DeleteConversationConfirmationModal';

export const ChatsHistoryTab = () => {
  const translate = useTranslate();
  const { conversations, isConversationsLoading } = useAiChatbotContext();
  const [focusedChatId, setFocusedChatId] = useState<string | null>(null);

  const chatGroups = useGroupedChatsByInterval(conversations ?? []);

  const handleFocusChange = useCallback((isFocused: boolean, element: HTMLElement) => {
    setFocusedChatId(isFocused ? element.id : null);
  }, []);

  const { listboxProps, ariaListbox } = useAriaListboxPattern<HTMLUListElement>({
    id: 'chats-history-items',
    handleFocusChange,
    enabled: !!chatGroups.length,
  });

  const focusChatsList = useCallback(() => {
    listboxProps.ref.current?.focus();
  }, [listboxProps.ref]);

  return (
    <ul className='p-4u pt-8u overflow-auto flex-1 scrollbar scrollbar-thumb' {...listboxProps}>
      {isConversationsLoading && (
        <div className='rounded-6u overflow-hidden'>
          <Skeleton count={4} inline containerClassName='flex flex-col gap-1u' className='h-24u' />
        </div>
      )}
      {!isConversationsLoading &&
        chatGroups.map(({ chats, label, key }) => (
          <div key={key}>
            <Paragraph className='text-interface-small leading-interface-small text-gray-tundora mb-4u mt-6u text-center'>
              {label}
            </Paragraph>
            <div className='rounded-6u overflow-hidden'>
              {chats.map((chat) => (
                <ChatItem
                  key={chat.id}
                  chat={chat}
                  ariaFocused={focusedChatId === chat.id}
                  focusChatsList={focusChatsList}
                  onDeleteButtonBlur={() => {
                    if (!listboxProps.ref.current) return;

                    ariaListbox?.setActiveDescendant(chat.id);
                    focusChatsList();
                  }}
                  onEditButtonBlur={(e) => {
                    if (!listboxProps.ref.current || listboxProps.ref.current !== e.relatedTarget) return;

                    ariaListbox?.setActiveDescendant(chat.id);
                    focusChatsList();
                  }}
                />
              ))}
            </div>
          </div>
        ))}
      {!isConversationsLoading && chatGroups.length === 0 && (
        <div className='flex flex-col items-center justify-center h-full'>
          <Paragraph className='text-interface-large leading-interface-large text-center'>
            {translate('aiChatbot.chatsHistory.noChats')}
          </Paragraph>
        </div>
      )}
    </ul>
  );
};

type ChatItemProps = {
  chat: AiChatbotConversation;
  ariaFocused: boolean;
  onEditButtonBlur: (e: FocusEvent<HTMLButtonElement, Element>) => void;
  onDeleteButtonBlur: () => void;
  focusChatsList: () => void;
};

const ChatItem = ({ chat, ariaFocused, onDeleteButtonBlur, onEditButtonBlur, focusChatsList }: ChatItemProps) => {
  const { translate, locale } = useLocalizationContext();
  const { confirm } = useConfirmationDialogContext();
  const { openEditChatNameModal, setSelectedView, setSelectedChat } = useAiChatbotContext();
  const { execute: deleteConversation } = useDeleteConversation();
  const { mutate } = useConversations();

  const handleClick = useCallback<MouseEventHandler<HTMLLIElement>>(
    (e) => {
      e.stopPropagation();
      setSelectedChat(chat);
      setSelectedView(AiChatbotView.ChatDetails);
    },
    [chat, setSelectedChat, setSelectedView],
  );

  const handleEditClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      e.stopPropagation();
      openEditChatNameModal(chat, mutate);
    },
    [chat, mutate, openEditChatNameModal],
  );

  const handleDeleteClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      e.stopPropagation();

      confirm(
        async () => {
          await deleteConversation(chat.id);
          await mutate();
          setTimeout(focusChatsList, 0);
        },
        ({ onClose, onConfirm }) => (
          <DeleteConversationConfirmationModal
            chatName={chat.title}
            onClose={() => {
              onClose();
              setTimeout(focusChatsList, 0);
            }}
            onConfirm={onConfirm}
          />
        ),
      );
    },
    [chat.id, chat.title, confirm, deleteConversation, focusChatsList, mutate],
  );

  return (
    <li
      id={chat.id}
      role='option'
      aria-selected={false}
      className='flex items-center gap-4u pl-7u pr-2u bg-white mb-1u last:mb-0 group min-h-24u cursor-pointer'
      onClick={handleClick}
    >
      <FolderIcon className='w-10u h-10u shrink-0' />
      <div className={'overflow-hidden'}>
        <ParagraphWithTooltip className='text-interface-regular leading-interface-regular'>
          {chat.title}
        </ParagraphWithTooltip>
      </div>
      <div className={'min-w-[3.9375rem] ml-auto'}>
        <Paragraph
          className={classNames(
            'text-interface-small leading-interface-small whitespace-nowrap text-gray-tundora block group-hover:hidden',
            { '!hidden': ariaFocused },
          )}
        >
          {translate('aiChatbot.chatsHistory.chatItem.questionsCount', {
            count: addNumberSeparator(chat.questionsCount, locale),
          })}
        </Paragraph>
        <div
          className={classNames('hidden group-hover:flex gap-2u', {
            '!flex': ariaFocused,
          })}
        >
          <Button
            className='!p-1u !w-20u !h-20u !rounded-2u'
            color='quaternary'
            size='small'
            icon={(_, color) => <PencilEditIcon className='w-9u h-9u' color={color} />}
            tooltip={translate('aiChatbot.chatsHistory.chatItem.editTooltip')}
            onClick={handleEditClick}
            onBlur={onEditButtonBlur}
          />
          <Button
            className='!p-1u !w-20u !h-20u !rounded-2u'
            color='destructive'
            size='small'
            icon={(_, color) => <TrashCanIcon className='w-9u h-9u' color={color} />}
            onClick={handleDeleteClick}
            onKeyDown={(e) => {
              if (e.key === 'Tab' && !e.shiftKey) {
                e.preventDefault();
                onDeleteButtonBlur();
              }
            }}
          />
        </div>
      </div>
    </li>
  );
};
